<template>
  <section class="section">
    <b-loading isFullPage :active="isLoading" />
    <div class="columns">
      <h1 class="title column is-size-5 has-text-color-7 mb-0">
        {{ App.ModelsScreen.DOCUMENT_MODELS }}
      </h1>
      <div class="header-buttons column">
        <b-button
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          class="has-text-weight-semibold spacing-py-15 spacing-mr-5"
          tag="router-link"
          iconLeft="plus"
          :expanded="isMobile"
          :to="newModel()"
          >{{ App.ModelsScreen.MODEL }}</b-button
        >
      </div>
    </div>

    <GenericPanelSearch
      :filterList="filters"
      :filterOptions="getFilterOptions"
      :requestBaseData="requestBaseData"
      :searchRequest="searchRequest"
      :tabPersistName="tabPersistName"
      :placeholder="App.ModelsScreen.PLACEHOLDER.SEARCH_BY"
      @filters="setFilterList"
      @search-value="setSearchValue"
    />

    <div class="level-left has-text-left spacing-mb-10">
      <span class="ml-10">
        {{ getPaginationDetails }}
      </span>
    </div>

    <section class="spacing-mb-20">
      <b-table
        class="custom-table-administration custom-3-columns-adm"
        :data="getModelList"
        :sortIcon="sortIcon"
        :sortIconSize="sortIconSize"
        :aria-next-label="General.NEXT_PAGE"
        :aria-previous-label="General.PREVIOUS_PAGE"
        :aria-page-label="General.PAGE"
        :aria-current-label="General.CURRENT_PAGE"
        :defaultSortDirection="defaultSortOrder"
        :defaultSort="[sortField, sortOrder]"
        backendSorting
        type="is-black"
        hoverable
      >
        <template>
          <b-table-column v-slot="props" :label="General.NAME">
            <div class="has-text-color-8">
              {{ `${props.row.name}` }}
            </div>
          </b-table-column>
          <b-table-column v-slot="props" :label="General.TYPE">
            <div class="has-text-color-8">
              {{ props.row.document_type.type }}
            </div>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="isMobile ? '' : General.ACTIONS"
          >
            <b-tooltip
              :label="General.EDIT"
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
            >
              <b-button
                class="custom-action-button"
                tag="router-link"
                :to="goToModel(props.row.id)"
              >
                <b-icon icon="square-edit-outline" />
              </b-button>
            </b-tooltip>
            <b-tooltip
              :label="General.DELETE"
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
            >
              <b-button
                class="custom-action-button"
                @click="callDialog(props.row.id)"
              >
                <b-icon icon="delete" />
              </b-button>
            </b-tooltip>
          </b-table-column>
        </template>

        <template #empty>
          <section class="section">
            <NoListMessage v-if="!isLoading" />
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </section>
        </template>
      </b-table>
    </section>

    <div
      class="is-flex is-align-items-center mb-40"
      :class="{ 'is-flex is-flex-direction-column-reverse': isMobile }"
    >
      <b-dropdown
        v-model="perPage"
        aria-role="list"
        position="is-top-right"
        :class="{ 'spacing-mb-40': isMobile }"
        :expanded="isMobile"
      >
        <template slot="trigger">
          <b-button class="custom-dropdown-button" :expanded="isMobile">
            {{ perPage }} {{ General.PER_PAGE.toLowerCase() }}

            <b-icon class="spacing-mx-5" icon="chevron-down" />
          </b-button>
        </template>
        <div class="spacing-p-5 has-text-centered">
          {{ General.RECORDS_FOR_PAGE }}
          <div class="list-page">
            <b-dropdown-item
              v-for="option in optionsPagination"
              :key="option"
              :value="option"
            >
              <div>
                {{ option }}
              </div>
            </b-dropdown-item>
          </div>
        </div>
      </b-dropdown>

      <div class="custom-pagination-container" :class="{ 'mb-10': isMobile }">
        <div class="column is-6">
          <b-pagination
            :total="getModel.count"
            :perPage="perPage"
            :current.sync="currentPage"
            rangeBefore="1"
            rangeAfter="1"
            order="is-centered"
            class="process-panel-pagination"
            rounded
            :iconPrev="prevIcon"
            :iconNext="nextIcon"
            :aria-next-label="General.NEXT_PAGE"
            :aria-previous-label="General.PREVIOUS_PAGE"
            :aria-page-label="General.PAGE"
            :aria-current-label="General.CURRENT_PAGE"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";
import { App, General } from "@/utils/constants";
import NoListMessage from "@/components/NoListMessage";
import ModalWarning from "@/views/App/ModalWarning.vue";
import GenericPanelSearch from "@/components/GenericPanelSearch";
import { formatPagination } from "@/utils/transformers";
import { TAB_PERSIST_NAME as PERSIST_NAME } from "@/utils/enumerate";

export default {
  name: "Models",

  components: {
    GenericPanelSearch,
    NoListMessage
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      perPage: 10,
      currentPage: 1,
      prevIcon: "chevron-left",
      nextIcon: "chevron-right",
      defaultSortOrder: "desc",
      sortIcon: "menu-up",
      sortIconSize: "is-medium",
      sortField: "date",
      sortOrder: "desc",
      modelSearch: "",
      typeSearch: {},
      filters: {},
      searchValue: ""
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      optionsPagination: "getItensPage",
      getAllModels: "getAllModels",
      getFileTypes: "getFileTypes",
      getModel: "getModelDocumentPaged",
      getDocType: "getFileTypesCacheById",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    fileTypes() {
      return this.getFileTypes.filter(type => type.layout_template);
    },

    getFilterOptions() {
      return {
        ORIGIN: {
          name: General.ORGAN,
          type: "ORIGIN",
          key: "origin",
          unique: true
        },
        UNITY: {
          name: General.UNITY,
          type: "UNITY",
          key: "unity",
          unique: true
        },
        DOCUMENT_TYPE: {
          name: General.DOCUMENT_TYPE,
          type: "DOCUMENT_TYPE",
          key: "document_type",
          unique: true
        }
      };
    },

    getPaginationDetails() {
      return formatPagination(
        this.getModel,
        this.currentPage,
        this.perPage,
        "SIMPLIFIED"
      );
    },

    requestBaseData() {
      return {
        page: this.currentPage,
        paginator_count: this.perPage,
        search: this.searchValue,
        partId: this.filters.origin?.id,
        capacityId: this.filters.unity?.id,
        document_type: this.filters.document_type?.id
      };
    },

    getModelList() {
      return this.getModel.list.map(model => ({
        ...model,
        document_type: this.getDocType(model.document_type) || { type: "" }
      }));
    },

    tabPersistName() {
      return `${PERSIST_NAME.DOCUMENT_MODELS}_${this.user.id}`;
    }
  },

  watch: {
    filters() {
      if (this.currentPage === 1) this.getModelDocument();
      else this.currentPage = 1;
    },
    currentPage: {
      handler() {
        this.getModelDocument();
      }
    },
    perPage: {
      handler() {
        this.getModelDocument();
      }
    }
  },

  async beforeMount() {
    await this.getModelDocument();
  },

  methods: {
    ...mapActions({
      getAllModelList: "getAllModelList",
      deleteModelRequest: "deleteModelRequest",
      requestModel: "requestModel"
    }),

    newModel() {
      return `editor-modelos`;
    },

    goToModel(id = false) {
      return `editor-modelos${typeof id === "number" ? "/" + id : ""}`;
    },

    getModelDocument: debounce(async function() {
      this.isLoading = true;
      await this.requestModel(this.requestBaseData);
      this.isLoading = false;
    }, 1000),

    callDialog(index) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.CONFIRM,
          message: `${this.General.MESSAGES.ARE_YOU_SURE_YOU_WANT_DELETE_MODEL}`,
          confirm: () => this.deleteModel(index)
        },
        buttons: [
          { label: General.YES, isConfirm: false, action: true },
          { label: General.NO, isConfirm: true, action: false }
        ],
        hasModalCard: true,
        trapFocus: true
      });
    },

    async deleteModel(modelId) {
      this.isLoading = true;
      await this.deleteModelRequest(modelId);
      await this.getModelDocument();
      this.isLoading = false;
    },

    setFilterList(values) {
      this.filters = values;
    },

    setSearchValue(value) {
      this.searchValue = value;
    },

    searchRequest(options) {
      this.currentPage = 1;
      this.requestModel(options);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";
.section {
  background-color: $color-1;
  padding-top: 20px;

  .header-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}

.document-type-button {
  min-width: 200px;
}
</style>
